import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Dropdown, Skeleton, Tooltip } from "antd";
import Fuse from "fuse.js";

import { useContextHospi } from "context/ContextHospi";
import {
  ChatStatus,
  IFulfilledChat,
  IMessageTextBody,
  TMessage
} from "interfaces/interfacesChat";
import { useSocket } from "context/SocketProvider";
import { ChatsList } from "components/Chat/ChatsList";
import { ChatMessagesList } from "components/Chat/ChatMessagesList";
import { ChatInputBox } from "components/Chat/ChatInputBox";
import { DialogWrapper } from "components/containers";
import { Button, TextPlain, TitleH2 } from "components/generic";
import Modal from "components/Modal";

import { useIsHost } from "utils/helpers";

import { AvatarImageChatBox } from "../ChatMessagesList/MessagesItemStyles";

import {
  ActivityInfo,
  BackButton,
  ChatLayout,
  ChatRoomStatusContainer,
  ChatsListContainer,
  ChatsListTitle,
  DropdownIcon,
  HeaderLeftSide,
  HeaderLeftSizePersonal,
  HeaderName,
  HeaderRightSide,
  HostVerificationContainer,
  HouseButton,
  MessagesListContainer,
  MessagesListHeader,
  NoSelectedChat,
  OnlineIndicator,
  OnlineIndicatorText,
  PersonalInfo,
  ProfileButtonContainer,
  RightSidebarContainer,
  SearchInput
} from "./styles";
import {
  getUserOnlineStatusQueryKey,
  useGetUserOnlineStatus
} from "API/queries/chatApiQueries";
import { useGetHouse } from "API/queries/houseApiQueries";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import {
  HOST_CHATS_ROUTE_ID,
  ROOM_INFO_ROUTE,
  STUDENT_CHATS_ROUTE_ID,
  STUDENT_ROUTE_ID
} from "utils/constants";
import { Tabs } from "../../Tabs";
import { useGetMatch } from "API/queries/studentApiQueries";
import { getChatRemoveByField } from "utils/chat";
import { StudentProfileChatBox } from "../StudentProfileChatBox";
import { formatDateTime, parseServerDateTime } from "utils/time";
import { HostAcceptRequestMessage } from "../ChatMessagesList/SystemMessages/HostAcceptRequestMessage";
import { RoomProfileChatBox } from "../RoomProfileChatBox";
import { useChat } from "context/ContextChat/ContextChat";
import { StudentAcceptRequestMessage } from "../ChatMessagesList/SystemMessages/StudentAcceptRequestMessage";
import { ChevronLeft } from "@styled-icons/bootstrap";
import { useIsMobile } from "../../../utils/hooks/useWindowSize";
import { useChatDropdownMenu } from "./hooks";
import { StudentChatRoomStatus } from "../RoomProfileChatBox/StudentChatRoomStatus";
import { StudentInviteBoxMobile } from "../../StudentInviteBox/StudentInviteBoxMobile";
import { getHouseName } from "../../../utils/house";
import { HostVerify } from "../../../pages/ProfileUserEdit/HostVerified";
import { useGetUserPreview } from "../../../API/queries/userApiQueries";

export enum ChatTabs {
  ACCEPTED = "accepted",
  REQUESTED = "requested",
  ARCHIVED = "archived"
}

export const ChatController = () => {
  const { userProfile } = useContextHospi();
  const [messageText, setMessageText] = useState("");
  const [searchString, setSearchString] = useState("");
  const [messageToDelete, setMessageToDelete] = useState<null | TMessage>(null);
  const [messageToEdit, setMessageToEdit] = useState<null | TMessage>(null);
  const params: { id?: string } = useParams();

  const [activeChatTab, setActiveChatTab] = useState<ChatTabs>(
    ChatTabs.REQUESTED
  );

  const navigate = useNavigate();
  const socket = useSocket();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation("chats");

  const isHost = useIsHost();

  const [fuseSearch, setFuseSearch] = useState<Fuse<IFulfilledChat> | null>(
    null
  );

  const [mobileViewMode, setMobileViewMode] = useState<
    "chats" | "messages" | null
  >(null);

  const {
    state: {
      chats,
      isLoadingChats,
      activeChatMessages: messages,
      isLoadingActiveChatMessages,
      isLoadingMoreChatMessages,
      chatCounters,
      activeChatId
    },
    loadMoreMessages,
    setActiveChatId,
    editMessage,
    deleteMessage,
    sendMessage
  } = useChat();

  const activeChat = chats.find((c) => c.chat_id === activeChatId);

  const filteredChats = useMemo(
    () =>
      chats.filter((chat) => {
        if (activeChatTab === ChatTabs.REQUESTED) {
          return (
            chat.chat_status === ChatStatus.REQUESTED &&
            !chat[getChatRemoveByField(userProfile?.user_type!)]
          );
        }
        if (activeChatTab === ChatTabs.ACCEPTED) {
          return (
            chat.chat_status === ChatStatus.ACCEPTED &&
            !chat[getChatRemoveByField(userProfile?.user_type!)]
          );
        }
        if (activeChatTab === ChatTabs.ARCHIVED) {
          return chat[getChatRemoveByField(userProfile?.user_type!)];
        }
        return false;
      }),
    [chats, activeChatTab, userProfile?.user_type]
  );

  const handleEditMessage = useCallback(
    (text: string) => {
      if (!text || !messageToEdit) return;

      editMessage({
        body: {
          text: text
        },
        author_id: messageToEdit.author_id,
        chat_id: messageToEdit.chat_id,
        created_at: messageToEdit.created_at
      });
      setMessageToEdit(null);
      setMessageText("");
    },
    [messageToEdit, editMessage]
  );

  const handleDeleteMessageClick = useCallback((message: TMessage) => {
    setMessageToDelete(message);
  }, []);

  const handleEditMessageClick = useCallback((message: TMessage) => {
    if ((message.body as IMessageTextBody)?.text) {
      setMessageToEdit(message);
      setMessageText((message.body as IMessageTextBody)?.text);
    }
  }, []);

  const handleDeleteMessage = useCallback(
    (message: TMessage) => {
      if (!message || !(message.body as IMessageTextBody)?.text) return;

      deleteMessage({
        body: {
          text: (message.body as IMessageTextBody)?.text
        },
        author_id: message.author_id,
        chat_id: message.chat_id,
        created_at: message.created_at
      });
      setMessageToDelete(null);
    },
    [deleteMessage]
  );

  const handleSubmitMessage = (messageText: string) => {
    if (!messageText) return;

    sendMessage({
      author_id: userProfile?.user_id!,
      chat_id: activeChatId!,
      body: { text: messageText }
    });

    setMessageText("");
  };

  const handleMessagesListScrollTop = useMemo(() => {
    return loadMoreMessages;
  }, [loadMoreMessages]);

  const {
    data: currentHouse,
    isLoading: isLoadingCurrentHouse,
    error: currentHouseError
  } = useGetHouse(activeChat?.house_id);

  const { dropdownMenuComponents, dropdownMenuItems } = useChatDropdownMenu({
    activeChat
  });

  const { data: match } = useGetMatch(
    activeChat?.student_id,
    activeChat?.host_id,
    activeChat?.house_id
  );

  const { data: lastUserOnlineStatus } = useGetUserOnlineStatus(
    activeChat?.conversation_partner_short?.user_id
  );

  useEffect(() => {
    if (activeChat?.conversation_partner_short?.user_id) {
      queryClient.invalidateQueries({
        queryKey: getUserOnlineStatusQueryKey(
          activeChat?.conversation_partner_short?.user_id
        )
      });
    }
  }, [queryClient, activeChat?.conversation_partner_short?.user_id]);

  const lastUserSeenFallback = activeChat
    ? isHost
      ? activeChat.student_most_recent_read_at
      : activeChat.host_most_recent_read_at
    : null;

  const lastUserSeenFinal =
    lastUserOnlineStatus?.last_seen ?? lastUserSeenFallback;

  useEffect(() => {
    const fuse = new Fuse(filteredChats, {
      includeScore: true,
      keys: [
        "conversation_partner_short.first_name",
        "conversation_partner_short.last_name"
      ],
      threshold: 0.4,
      distance: 100
    });
    setFuseSearch(fuse);
  }, [filteredChats]);

  const filteredChatIds = useMemo(() => {
    return searchString
      ? fuseSearch?.search(searchString).map((c) => c.item.chat_id)
      : null;
  }, [fuseSearch, searchString]);

  const navigateToChat = useCallback(
    (id: string | null) => {
      navigate(
        generatePath(isHost ? HOST_CHATS_ROUTE_ID : STUDENT_CHATS_ROUTE_ID, {
          lng: i18n.language,
          id: id ?? ""
        })
      );
      if (mobileViewMode) {
        setMobileViewMode("messages");
      }
    },
    [isHost, i18n.language, navigate, mobileViewMode]
  );

  useEffect(() => {
    setActiveChatId(!params.id || params.id === "all" ? null : params.id);
  }, [setActiveChatId, params.id]);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      setMobileViewMode(() => {
        return activeChatId ? "messages" : "chats";
      });
    } else {
      setMobileViewMode(null);
    }
  }, [isMobile, activeChatId]);

  const houseTitle =
    currentHouse && activeChat
      ? getHouseName(
          currentHouse.kind!,
          isHost
            ? {
                first_name: userProfile?.first_name!,
                last_name: userProfile?.last_name!
              }
            : {
                first_name: activeChat.conversation_partner_short?.first_name!,
                last_name: activeChat.conversation_partner_short?.last_name!
              }
        )
      : "";

  const conversationPartnerFullName = activeChat
    ? `${activeChat.conversation_partner_short?.first_name} ${activeChat.conversation_partner_short?.last_name}`
    : "";

  const { data: conversationPartnerFull } = useGetUserPreview(
    activeChat?.conversation_partner_short?.user_id
  );

  return (
    <ChatLayout>
      <ChatsListContainer $collapsed={mobileViewMode === "messages"}>
        <ChatsListTitle>{t("chatController.matching")}</ChatsListTitle>
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "20px"
          }}
        >
          <Tabs
            value={activeChatTab}
            onChange={(val) => {
              setActiveChatTab(val as ChatTabs);
              setSearchString("");
            }}
            tabsCount={3}
          >
            <Tabs.Tab value={ChatTabs.REQUESTED}>
              {t("chatController.requests")}{" "}
              {chatCounters?.requested && chatCounters?.requested > 0 ? (
                <>({chatCounters?.requested})</>
              ) : null}
            </Tabs.Tab>
            <Tabs.Tab value={ChatTabs.ACCEPTED}>
              {t("chatController.chat")}{" "}
              {chatCounters?.unread_accepted &&
              chatCounters?.unread_accepted > 0 ? (
                <>({chatCounters?.unread_accepted})</>
              ) : null}
            </Tabs.Tab>
            <Tabs.Tab value={ChatTabs.ARCHIVED}>
              {t("chatController.archived")}
            </Tabs.Tab>
          </Tabs>
        </div>
        <SearchInput
          placeholder={t("chatController.search")}
          prefix={<SearchOutlined />}
          value={searchString}
          allowClear={true}
          onChange={(e) => {
            setSearchString(e.target.value);
          }}
        />
        <ChatsList
          chats={
            filteredChatIds
              ? filteredChatIds.reduce<IFulfilledChat[]>((acc, id) => {
                  const chat = filteredChats.find((c) => id === c.chat_id);
                  if (chat) {
                    return [...acc, chat];
                  }
                  return acc;
                }, [])
              : filteredChats
          }
          selectedChatId={activeChatId}
          isLoading={isLoadingChats}
          filter={searchString}
          onChatClick={useCallback(
            (chat) => {
              navigateToChat(chat.chat_id);
            },
            [navigateToChat]
          )}
        />
      </ChatsListContainer>
      <MessagesListContainer $collapsed={mobileViewMode === "chats"}>
        {activeChatId && activeChat ? (
          <>
            <MessagesListHeader>
              {mobileViewMode ? (
                <BackButton
                  onClick={() => {
                    setMobileViewMode("chats");
                  }}
                >
                  <ChevronLeft />
                </BackButton>
              ) : null}
              <HeaderLeftSide>
                {activeChat.conversation_partner_short?.user_id ? (
                  <HeaderLeftSizePersonal>
                    <AvatarImageChatBox
                      onClick={() => {
                        if (!isHost) return;

                        navigate(
                          generatePath(STUDENT_ROUTE_ID, {
                            lng: i18n.language,
                            id: activeChat.conversation_partner_short!.user_id
                          })
                        );
                      }}
                      src={activeChat.conversation_partner_short?.thumbnail_url}
                    />
                    <PersonalInfo>
                      <HouseButton
                        type="link"
                        title={houseTitle}
                        onClick={() => {
                          if (isHost) {
                            navigate(
                              generatePath(STUDENT_ROUTE_ID, {
                                lng: i18n.language,
                                id: activeChat.conversation_partner_short!
                                  .user_id
                              })
                            );
                          } else {
                            if (!currentHouse) return;

                            const url = `${generatePath(ROOM_INFO_ROUTE, {
                              lng: i18n.language
                            })}/${currentHouse.house_id}`;
                            navigate(url);
                          }
                        }}
                      >
                        <HeaderName title={conversationPartnerFullName}>
                          {conversationPartnerFullName}
                        </HeaderName>
                      </HouseButton>
                    </PersonalInfo>

                    {isLoadingCurrentHouse ? (
                      <Skeleton.Button block active />
                    ) : null}
                  </HeaderLeftSizePersonal>
                ) : null}

                {currentHouse ? (
                  <Tooltip
                    title={t("chatController.openRoom")}
                    placement="right"
                  >
                    <HouseButton
                      type="link"
                      title={houseTitle}
                      onClick={() => {
                        if (!currentHouse) return;

                        const url = `${generatePath(ROOM_INFO_ROUTE, {
                          lng: i18n.language
                        })}/${currentHouse.house_id}`;
                        navigate(url);
                      }}
                    >
                      {houseTitle}
                    </HouseButton>
                  </Tooltip>
                ) : null}
                {currentHouseError ? (
                  <div
                    style={{
                      color: "red"
                    }}
                  >
                    {(currentHouseError as any).toString()}
                  </div>
                ) : null}

                {lastUserOnlineStatus ? (
                  <ActivityInfo>
                    {lastUserOnlineStatus.online ? (
                      <>
                        {t("chatController.active")}{" "}
                        <OnlineIndicator isOnline={true} />
                      </>
                    ) : (
                      <>
                        <OnlineIndicator isOnline={false} />
                        <OnlineIndicatorText>
                          {mobileViewMode
                            ? lastUserSeenFinal
                              ? formatDateTime(
                                  parseServerDateTime(lastUserSeenFinal),
                                  { minimizeOutputLength: true }
                                )
                              : t("chatController.offline")
                            : lastUserSeenFinal
                            ? t("chatController.lastSeen") +
                              formatDateTime(
                                parseServerDateTime(lastUserSeenFinal),
                                { minimizeOutputLength: true }
                              )
                            : t("chatController.offline")}
                        </OnlineIndicatorText>
                      </>
                    )}
                  </ActivityInfo>
                ) : (
                  <Skeleton.Button block active />
                )}
              </HeaderLeftSide>
              {conversationPartnerFull?.user_type === "host" ? (
                !isMobile ? (
                  <HostVerificationContainer>
                    <div>
                      {conversationPartnerFull.hosted_tenants_count &&
                      conversationPartnerFull.hosted_tenants_count > 0 ? (
                        <HostVerify verification_status={"experienced"} />
                      ) : null}
                      {conversationPartnerFull.verification_status ===
                      "verified" ? (
                        <HostVerify
                          verification_status={"verified"}
                          showLabel={true}
                          chat_environment={true}
                        />
                      ) : null}
                    </div>
                  </HostVerificationContainer>
                ) : null
              ) : null}

              <div style={{ flexGrow: 1 }} />
              <HeaderRightSide>
                <ProfileButtonContainer>
                  <Dropdown menu={{ items: dropdownMenuItems }}>
                    <DropdownIcon />
                  </Dropdown>
                </ProfileButtonContainer>
                {match && isHost && activeChat.conversation_partner_short ? (
                  <ChatRoomStatusContainer>
                    <StudentInviteBoxMobile
                      match={match}
                      student={activeChat.conversation_partner_short}
                    />
                  </ChatRoomStatusContainer>
                ) : null}
                {match && !isHost ? (
                  <ChatRoomStatusContainer>
                    <StudentChatRoomStatus match={match} size={"middle"} />
                  </ChatRoomStatusContainer>
                ) : null}
              </HeaderRightSide>
            </MessagesListHeader>
            <ChatMessagesList
              chat={activeChat}
              messagesList={messages}
              isLoading={isLoadingActiveChatMessages}
              isLoadingPreviousPage={isLoadingMoreChatMessages}
              onEditMessage={handleEditMessageClick}
              onDeleteMessage={handleDeleteMessageClick}
              onScrollTop={handleMessagesListScrollTop}
            />
            {activeChat.chat_status === ChatStatus.ACCEPTED ? (
              <ChatInputBox
                isSendButtonDisabled={!activeChatId || !socket.isConnected}
                isDisabled={false}
                isEditMode={Boolean(messageToEdit)}
                textValue={messageText}
                onTextValueChange={(v) => setMessageText(v)}
                onSubmit={handleSubmitMessage}
                onEdit={handleEditMessage}
                onCancelEdit={() => {
                  setMessageToEdit(null);
                  setMessageText("");
                }}
              />
            ) : null}
            {isHost && activeChat.chat_status === ChatStatus.REQUESTED ? (
              <HostAcceptRequestMessage chatId={activeChatId} t={t} />
            ) : null}
            {!isHost && activeChat.chat_status === ChatStatus.REQUESTED ? (
              <StudentAcceptRequestMessage t={t} />
            ) : null}
          </>
        ) : (
          <NoSelectedChat>{t("chatController.selectChat")}</NoSelectedChat>
        )}
      </MessagesListContainer>
      <RightSidebarContainer>
        {isHost && currentHouse && activeChat && match ? (
          <StudentProfileChatBox
            house={currentHouse}
            chat={activeChat}
            match={match}
          />
        ) : null}
        {!isHost && currentHouse && activeChat && match ? (
          <RoomProfileChatBox
            houseTitle={houseTitle}
            house={currentHouse}
            match={match}
          />
        ) : null}
      </RightSidebarContainer>
      {messageToDelete && (
        <Modal isShow={true} handleClose={() => setMessageToDelete(null)}>
          <DialogWrapper>
            <TitleH2>{t("deleteMsg")}</TitleH2>
            <TextPlain>{t("textDelete")}</TextPlain>
            <div className="buttons">
              <Button normal onClick={() => setMessageToDelete(null)}>
                {t("butCancel")}
              </Button>
              <Button
                danger
                onClick={() => {
                  handleDeleteMessage(messageToDelete);
                }}
              >
                {t("butDelete")}
              </Button>
            </div>
          </DialogWrapper>
        </Modal>
      )}
      {dropdownMenuComponents}
    </ChatLayout>
  );
};
